import { fromJS } from 'immutable'
import { merge, pick } from 'lodash-es'
import { AnyAction } from 'redux'
import { createReducer } from 'redux-immutablejs'

import {
  CardSide,
  HealthPlanKeys,
  OcrStatus,
  PAYMENT_CARD_TYPES,
  PaymentMethod,
  SecondaryHealthPlanKeys,
  UploadStatus,
} from '@lyrahealth-inc/shared-app-logic'

import {
  GET_ELIGIBILITY_OPTIONS,
  GET_HEALTHPLAN_PAYMENT,
  GET_PAYMENT_HISTORY,
  GET_UNRESOLVED_INFRACTIONS,
  POLL_HEALTH_PLAN_CARD_OCR_FULFILLED,
  POLL_HEALTH_PLAN_CARD_OCR_PENDING,
  POLL_HEALTH_PLAN_CARD_OCR_REJECTED,
  REMOVE_HEALTHPLAN_INFO,
  REMOVE_HEALTHPLAN_PAYMENT,
  REMOVE_INFRACTION_PAYMENT,
  RESET_CARD_UPLOAD_STATE,
  SET_HEALTHPLAN_DATA,
  SET_PATIENT_DATA,
  START_HEALTHPLAN_AND_INFRACTION_CARD_SETUP,
  STOP_HEALTHPLAN_AND_INFRACTION_CARD_SETUP,
  SUBMIT_HEALTHPLAN_INFO,
  SUBMIT_HEALTHPLAN_PAYMENT,
  SUBMIT_SECONDARY_HEALTHPLAN_INFO,
  UPDATE_HEALTHPLAN_AND_INFRACTION_CARD_REQUEST_TYPE,
  UPDATE_HEALTHPLAN_PAYMENT,
  UPLOAD_HEALTH_PLAN_CARD_FULFILLED,
  UPLOAD_HEALTH_PLAN_CARD_PENDING,
  UPLOAD_HEALTH_PLAN_CARD_REJECTED,
} from './healthPlanActions'

interface UploadHealthPlanCardPendingAction {
  type: typeof UPLOAD_HEALTH_PLAN_CARD_PENDING
  cardSide: CardSide
}

interface UploadHealthPlanCardFulfilledAction {
  type: typeof UPLOAD_HEALTH_PLAN_CARD_FULFILLED
  payload: { taskId: string; status: string }
  cardSide: CardSide
}

interface PaymentCardFields {
  infractionCardLast4Type: PAYMENT_CARD_TYPES | null
  infractionCardLast4: string | null
  infractionCardBrand: string | null
  cardLast4Type: PAYMENT_CARD_TYPES | null
  cardLast4: string | null
  cardBrand: string | null
  selfPayCardLast4Type: PAYMENT_CARD_TYPES | null
  selfPayCardLast4: string | null
  selfPayCardBrand: string | null
}

interface UploadHealthPlanCardRejectedAction {
  type: typeof UPLOAD_HEALTH_PLAN_CARD_REJECTED
  error: string
  cardSide: CardSide
}

interface PollHealthPlanCardOcrPendingAction {
  type: typeof POLL_HEALTH_PLAN_CARD_OCR_PENDING
  cardSide: CardSide
}

interface PollHealthPlanCardOcrFulfilledAction {
  type: typeof POLL_HEALTH_PLAN_CARD_OCR_FULFILLED
  payload: { status: string; extractedValues?: Record<string, string> }
  cardSide: CardSide
}

interface PollHealthPlanCardOcrRejectedAction {
  type: typeof POLL_HEALTH_PLAN_CARD_OCR_REJECTED
  error: string
  cardSide: CardSide
}

const initialCardUploadState = {
  frontCard: {
    status: UploadStatus.IDLE,
    taskId: null,
    error: null,
    extractedValues: null,
  },
  backCard: {
    status: UploadStatus.IDLE,
    taskId: null,
    error: null,
  },
}

const $$initialState = fromJS({
  cardLast4: null,
  infractionCardLast4: null,
  cardLast4Type: null,
  infractionCardLast4Type: null,
  HPAndInfractionCardReminderType: 'modal',
  cardUpload: initialCardUploadState,
})

const hpInfoKeys = [
  'customerPropertiesMap.healthPlanExtensionEnabled', // customer enables using health plan
  'customerPropertiesMap.healthPlanSessionLimit', // eg - 25
  'customerPropertiesMap.healthPlanExtensionNotificationThreshold', // delta value subtracted from healthPlanSessionLimit to determine when to notify user
  'healthPlansList', // insurance plans for the customer
  'usedSessionCount', // number of EAP (free session) used
  'usedSessionCountByProgram', // number of EAP (free session) used for each program (e.g. coaching, therapy)
  'sessionHistory', // EAP session history
  'isUsingHealthPlan', // EAP (free session) vs HP
  'isEligibleForHealthPlanExtension', // healthPlanExtensionEnabled && (insurancePlan.isEligible || insurancePlan.isUnknown)
  'receiveHealthPlanPrompt', // should the client be prompted to enter HP info
]

const paymentKeys = [
  // 'healthPlanPaymentAccountToken',
  'cardLast4',
  'infractionCardLast4',
  'selfPayCardLast4',
]

const userInfoKeys = ['user.username']

const healthPlanDataReducer = ($$state: $TSFixMe, action: $TSFixMe) => {
  let healthPlanData = pick(action.data, [
    ...hpInfoKeys,
    ...HealthPlanKeys,
    ...SecondaryHealthPlanKeys,
    ...paymentKeys,
    ...userInfoKeys,
  ])
  // flatten
  healthPlanData = merge(healthPlanData, healthPlanData.customerPropertiesMap)
  delete healthPlanData.customerPropertiesMap
  $$state = $$state.merge(fromJS(healthPlanData))
  return $$state
}

export default createReducer($$initialState, {
  [SET_HEALTHPLAN_DATA]: healthPlanDataReducer,
  [SET_PATIENT_DATA]: ($$state: $TSFixMe, action: $TSFixMe) => {
    $$state = healthPlanDataReducer($$state, action)
    $$state = $$state.set('isPatientDataFetched', true)
    return $$state
  },

  [START_HEALTHPLAN_AND_INFRACTION_CARD_SETUP]: ($$state: $TSFixMe) => {
    $$state = $$state.set('showConfirmationScreen', true)
    return $$state
  },
  [STOP_HEALTHPLAN_AND_INFRACTION_CARD_SETUP]: ($$state: $TSFixMe) => {
    $$state = $$state.set('showConfirmationScreen', false)
    return $$state
  },
  [UPDATE_HEALTHPLAN_AND_INFRACTION_CARD_REQUEST_TYPE]: ($$state: $TSFixMe, action: $TSFixMe) => {
    $$state = $$state.set('HPAndInfractionCardReminderType', action.val)
    return $$state
  },
  [SUBMIT_HEALTHPLAN_INFO]: ($$state: $TSFixMe, action: $TSFixMe) => {
    const updatedInfo = pick(action.payload.data, HealthPlanKeys)

    $$state = $$state.merge(fromJS(updatedInfo))
    return $$state
  },
  [SUBMIT_SECONDARY_HEALTHPLAN_INFO]: ($$state: $TSFixMe, action: $TSFixMe) => {
    const updatedInfo = pick(action.payload.data, SecondaryHealthPlanKeys)

    $$state = $$state.merge(fromJS(updatedInfo))
    return $$state
  },
  [REMOVE_HEALTHPLAN_INFO]: ($$state: $TSFixMe) => {
    $$state = [...HealthPlanKeys, ...SecondaryHealthPlanKeys].reduce((map, key) => map.delete(key), $$state)
    return $$state
  },
  [SUBMIT_HEALTHPLAN_PAYMENT]: ($$state: $TSFixMe, action: $TSFixMe) => {
    const submittedInfo = pick(action.payload?.data, paymentKeys)

    $$state = $$state.merge(fromJS(submittedInfo))
    return $$state
  },
  [UPDATE_HEALTHPLAN_PAYMENT]: ($$state: $TSFixMe, action: $TSFixMe) => {
    const updatedInfo = pick(action.payload?.data, paymentKeys)

    $$state = $$state.merge(fromJS(updatedInfo))
    return $$state
  },
  [REMOVE_HEALTHPLAN_PAYMENT]: ($$state: $TSFixMe) => {
    $$state = $$state.delete('cardLast4')
    $$state = $$state.delete('cardLast4Type')
    return $$state
  },
  [REMOVE_INFRACTION_PAYMENT]: ($$state: $TSFixMe) => {
    $$state = $$state.delete('infractionCardLast4')
    $$state = $$state.delete('infractionCardLast4Type')
    return $$state
  },
  [GET_PAYMENT_HISTORY]: ($$state: $TSFixMe, action: $TSFixMe) => {
    $$state = $$state.set('paymentHistory', fromJS(action.payload.data))
    return $$state
  },
  [GET_ELIGIBILITY_OPTIONS]: ($$state: $TSFixMe, action: $TSFixMe) => {
    $$state = $$state.set('eligibilityOptions', fromJS(action.data))
    return $$state
  },
  [GET_UNRESOLVED_INFRACTIONS]: ($$state: Map<string, any>, action: AnyAction) => {
    $$state = $$state.set('unresolvedInfractions', fromJS(action.data))
    return $$state
  },
  [GET_HEALTHPLAN_PAYMENT]: ($$state: $TSFixMe, action: AnyAction & { payload: { data: PaymentMethod[] } }) => {
    const cards = action.payload.data

    // Reset all card fields first to ensure we don't show stale data
    const resetCardFields: PaymentCardFields = {
      infractionCardLast4Type: null,
      infractionCardLast4: null,
      infractionCardBrand: null,
      cardLast4Type: null,
      cardLast4: null,
      cardBrand: null,
      selfPayCardLast4Type: null,
      selfPayCardLast4: null,
      selfPayCardBrand: null,
    }
    $$state = $$state.merge(fromJS(resetCardFields))

    cards?.forEach((card: PaymentMethod) => {
      if (card.infractionConsent && card.infractionCardLast4) {
        $$state = $$state.set('infractionCardLast4Type', card.paymentType)
        $$state = $$state.set('infractionCardLast4', card.infractionCardLast4)
        $$state = $$state.set('infractionCardBrand', card.brand)
      }
      if (card.costSharingCardLast4) {
        $$state = $$state.set('cardLast4Type', card.paymentType)
        $$state = $$state.set('cardLast4', card.costSharingCardLast4)
        $$state = $$state.set('cardBrand', card.brand)
      }
      if (card.selfPayCardLast4) {
        $$state = $$state.set('selfPayCardLast4Type', card.paymentType)
        $$state = $$state.set('selfPayCardLast4', card.selfPayCardLast4)
        $$state = $$state.set('selfPayCardBrand', card.brand)
      }
    })
    return $$state
  },
  [UPLOAD_HEALTH_PLAN_CARD_PENDING]: ($$state: $TSFixMe, action: UploadHealthPlanCardPendingAction) => {
    const cardKey = action.cardSide === CardSide.FRONT ? 'frontCard' : 'backCard'
    return $$state.setIn(
      ['cardUpload', cardKey],
      fromJS({
        status: UploadStatus.UPLOAD_PROCESSING,
        taskId: null,
        error: null,
        ...(action.cardSide === CardSide.FRONT ? { extractedValues: null } : {}),
      }),
    )
  },
  [UPLOAD_HEALTH_PLAN_CARD_FULFILLED]: ($$state: $TSFixMe, action: UploadHealthPlanCardFulfilledAction) => {
    const cardKey = action.cardSide === CardSide.FRONT ? 'frontCard' : 'backCard'
    return $$state
      .setIn(['cardUpload', cardKey, 'status'], UploadStatus.UPLOADED)
      .setIn(['cardUpload', cardKey, 'taskId'], action.payload.taskId)
      .setIn(['cardUpload', cardKey, 'error'], null)
  },
  [UPLOAD_HEALTH_PLAN_CARD_REJECTED]: ($$state: $TSFixMe, action: UploadHealthPlanCardRejectedAction) => {
    const cardKey = action.cardSide === CardSide.FRONT ? 'frontCard' : 'backCard'
    return $$state
      .setIn(['cardUpload', cardKey, 'status'], UploadStatus.UPLOAD_FAILED)
      .setIn(['cardUpload', cardKey, 'error'], action.error)
  },
  [POLL_HEALTH_PLAN_CARD_OCR_PENDING]: ($$state: $TSFixMe, action: PollHealthPlanCardOcrPendingAction) => {
    const cardKey = action.cardSide === CardSide.FRONT ? 'frontCard' : 'backCard'
    return $$state.setIn(['cardUpload', cardKey, 'status'], OcrStatus.OCR_PROCESSING)
  },
  [POLL_HEALTH_PLAN_CARD_OCR_FULFILLED]: ($$state: $TSFixMe, action: PollHealthPlanCardOcrFulfilledAction) => {
    const cardKey = action.cardSide === CardSide.FRONT ? 'frontCard' : 'backCard'
    let newState = $$state.setIn(['cardUpload', cardKey, 'status'], OcrStatus.OCR_COMPLETED)
    if (action.cardSide === CardSide.FRONT && action.payload?.extractedValues) {
      newState = newState.setIn(['cardUpload', cardKey, 'extractedValues'], fromJS(action.payload.extractedValues))
    }
    return newState
  },
  [POLL_HEALTH_PLAN_CARD_OCR_REJECTED]: ($$state: $TSFixMe, action: PollHealthPlanCardOcrRejectedAction) => {
    const cardKey = action.cardSide === CardSide.FRONT ? 'frontCard' : 'backCard'
    return $$state
      .setIn(['cardUpload', cardKey, 'status'], OcrStatus.OCR_FAILED)
      .setIn(['cardUpload', cardKey, 'error'], action.error)
  },
  [RESET_CARD_UPLOAD_STATE]: ($$state: $TSFixMe) => {
    return $$state.set('cardUpload', fromJS(initialCardUploadState))
  },
})
